class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.pendingRequest = false;
    this.giftSyncing = false;
    this.boundDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('scroll', this.onScroll.bind(this), true);
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    document.addEventListener('keydown', this.boundDocumentKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundDocumentKeydown);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (!this.classList.contains('active')) {
      this.classList.add('animate');
      requestAnimationFrame(() => this.classList.add('active'));
      document.body.classList.add('overflow-hidden');
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
      const focusAfterOpen = () => this.activateFocusTrap();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        focusAfterOpen();
      } else {
        this.querySelector('.drawer__inner')?.addEventListener('transitionend', focusAfterOpen, { once: true });
      }
    }
    this.queueEnhancements();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.activeElement);
  }

  activateFocusTrap(preferredElement) {
    const dialog = this.querySelector('.drawer__inner');
    if (!dialog || !this.classList.contains('active')) return;
    const focusElement = preferredElement || dialog.querySelector('.drawer__close') || dialog;
    trapFocus(dialog, focusElement);
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('active')) {
      event.preventDefault();
      this.close();
    }
  }

  onClick(event) {
    const closeControl = event.target.closest('[data-cart-close]');
    if (closeControl) {
      event.preventDefault();
      this.close();
      return;
    }

    const previous = event.target.closest('[data-cart-carousel-prev]');
    const next = event.target.closest('[data-cart-carousel-next]');
    if (previous || next) {
      const carousel = (previous || next).closest('[data-cart-recommendations]')?.querySelector('[data-cart-carousel]');
      if (!carousel) return;
      const card = carousel.querySelector('.stll-cart__recommendation');
      const distance = (card?.getBoundingClientRect().width || carousel.clientWidth * 0.82) + 12;
      carousel.scrollBy({ left: previous ? -distance : distance, behavior: 'smooth' });
      return;
    }

    const addButton = event.target.closest('[data-cart-upsell-add]');
    if (addButton) {
      event.preventDefault();
      this.addUpsell(addButton);
      return;
    }

    const removeDiscount = event.target.closest('[data-cart-remove-discount]');
    if (removeDiscount) {
      event.preventDefault();
      this.removeDiscount(removeDiscount.dataset.cartRemoveDiscount, removeDiscount);
    }
  }

  onChange(event) {
    if (event.target.matches('.stll-cart__subscription-input')) this.updateSellingPlan(event.target);
  }

  onSubmit(event) {
    const form = event.target.closest('[data-cart-discount-form]');
    if (!form) return;
    event.preventDefault();
    this.applyDiscount(form);
  }

  onScroll(event) {
    if (event.target.matches?.('[data-cart-carousel]')) this.updateCarouselButtons(event.target);
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  renderContents(parsedState, options = {}) {
    if (!parsedState?.sections) return;
    const wasOpen = this.classList.contains('active');

    for (const section of this.getSectionsToRender()) {
      const html = parsedState.sections[section.id];
      if (!html) continue;
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const source = this.getSectionDOM(html, section.selector || '.shopify-section');
      if (!target || !source) continue;
      target.innerHTML = source.innerHTML;

      if (section.id === 'cart-drawer') {
        const sourceDrawer = this.getSectionDOM(html, 'cart-drawer');
        this.classList.toggle('is-empty', sourceDrawer?.classList.contains('is-empty') || false);
        this.dataset.freeGiftEnabled = sourceDrawer?.dataset.freeGiftEnabled || 'false';
        this.dataset.freeGiftThreshold = sourceDrawer?.dataset.freeGiftThreshold || '0';
        this.dataset.freeGiftVariantId = sourceDrawer?.dataset.freeGiftVariantId || '';
        this.dataset.firstProductId = sourceDrawer?.dataset.firstProductId || '';
      }
    }

    if (wasOpen) {
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      this.activateFocusTrap(this.querySelector('.drawer__close'));
    } else if (options.open !== false) {
      this.open();
    }
    this.queueEnhancements();
  }

  async refresh(options = {}) {
    const wasOpen = this.classList.contains('active');
    const url = new URL(window.routes.cart_url, window.location.origin);
    url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    url.searchParams.set('sections_url', window.location.pathname);
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(window.cartStrings?.error || 'Cart error');
    const sections = await response.json();
    this.renderContents({ sections }, { open: options.open ?? wasOpen });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  sectionPayload(payload = {}) {
    return {
      ...payload,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }

  async request(url, payload, { render = true } = {}) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(this.sectionPayload(payload)),
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || window.cartStrings?.error || 'Cart error');
    }
    if (render && data.sections) this.renderContents(data);
    return data;
  }

  async getCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(window.cartStrings?.error || 'Cart error');
    return response.json();
  }

  setBusy(isBusy, sourceButton) {
    this.pendingRequest = isBusy;
    this.toggleAttribute('aria-busy', isBusy);
    this.querySelectorAll('button, input').forEach((control) => {
      if (control.matches('[data-cart-close]')) return;
      control.disabled = isBusy;
    });
    if (sourceButton) sourceButton.classList.toggle('is-loading', isBusy);
  }

  showError(message, target) {
    const error = target || this.querySelector('#CartDrawer-CartErrors');
    if (error) error.textContent = message || window.cartStrings?.error || '';
    const { CartErrorEvent } = window.StandardEvents || {};
    if (CartErrorEvent && message) this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
  }

  queueEnhancements() {
    queueMicrotask(() => {
      this.syncFreeGift();
      this.loadRecommendations();
      this.querySelectorAll('[data-cart-carousel]').forEach((carousel) => this.updateCarouselButtons(carousel));
    });
  }

  async updateSellingPlan(input) {
    if (this.pendingRequest) return;
    const lineKey = input.dataset.lineKey;
    const quantity = Number(input.dataset.quantity) || 1;
    const sellingPlan = input.checked ? input.dataset.sellingPlanId : null;
    const deferred = this.createLineEvent('update', { id: lineKey, quantity });
    this.setBusy(true, input);
    try {
      const data = await this.request(window.routes.cart_change_url, {
        id: lineKey,
        quantity,
        selling_plan: sellingPlan,
      });
      const cart = await this.getCart();
      this.resolveLineEvent(deferred, cart);
      this.publishUpdate(cart);
      return data;
    } catch (error) {
      input.checked = !input.checked;
      deferred?.reject(error);
      this.showError(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  activeDiscountCodes() {
    return Array.from(this.querySelectorAll('[data-cart-remove-discount]'))
      .map((button) => button.dataset.cartRemoveDiscount)
      .filter(Boolean);
  }

  async applyDiscount(form) {
    if (this.pendingRequest) return;
    const input = form.querySelector('input[name="discount"]');
    const button = form.querySelector('button[type="submit"]');
    const message = form.parentElement.querySelector('[data-cart-discount-message]');
    const code = input.value.trim();
    if (!code) {
      message.textContent = form.dataset.emptyMessage;
      message.classList.add('is-error');
      input.focus();
      return;
    }

    const codes = Array.from(new Set([...this.activeDiscountCodes(), code]));
    const deferred = this.createDiscountEvent(codes);
    this.setBusy(true, button);
    message.textContent = '';
    message.classList.remove('is-error');
    try {
      const data = await this.request(window.routes.cart_update_url, { discount: codes.join(',') });
      const applications = data.cart_level_discount_applications || [];
      const applied = applications.some((discount) => discount.title?.toLowerCase() === code.toLowerCase());
      if (!applied) throw new Error(form.dataset.invalidMessage);
      input.value = '';
      this.resolveDiscountEvent(deferred, data);
      this.publishUpdate(data);
    } catch (error) {
      deferred?.reject(error);
      const currentForm = this.querySelector('[data-cart-discount-form]') || form;
      const currentMessage = currentForm.parentElement.querySelector('[data-cart-discount-message]');
      currentMessage.textContent = error.message || currentForm.dataset.errorMessage;
      currentMessage.classList.add('is-error');
      this.showError('', null);
    } finally {
      this.setBusy(false);
    }
  }

  async removeDiscount(code, button) {
    if (this.pendingRequest) return;
    const codes = this.activeDiscountCodes().filter((activeCode) => activeCode !== code);
    const deferred = this.createDiscountEvent(codes);
    this.setBusy(true, button);
    try {
      const data = await this.request(window.routes.cart_update_url, { discount: codes.join(',') });
      this.resolveDiscountEvent(deferred, data);
      this.publishUpdate(data);
    } catch (error) {
      deferred?.reject(error);
      this.showError(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  async addUpsell(button) {
    if (this.pendingRequest) return;
    const variantId = button.dataset.cartUpsellAdd;
    const deferred = this.createLineEvent('add', { merchandiseId: variantId, quantity: 1 });
    this.setBusy(true, button);
    try {
      await this.request(window.routes.cart_add_url, { items: [{ id: variantId, quantity: 1 }] });
      const cart = await this.getCart();
      this.resolveLineEvent(deferred, cart);
      this.publishUpdate(cart);
    } catch (error) {
      deferred?.reject(error);
      this.showError(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  async syncFreeGift() {
    if (this.giftSyncing || this.dataset.freeGiftEnabled !== 'true') return;
    const threshold = Number(this.dataset.freeGiftThreshold) || 0;
    const variantId = this.dataset.freeGiftVariantId;
    if (!threshold || !variantId) return;

    this.giftSyncing = true;
    try {
      const cart = await this.getCart();
      const giftLines = cart.items.filter((item) => String(item.properties?._free_gift) === 'true');
      const regularTotal = cart.items
        .filter((item) => String(item.properties?._free_gift) !== 'true')
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const giftIsCharged = giftLines.some((item) => item.final_line_price > 0);

      if (giftLines.length && (regularTotal < threshold || giftIsCharged)) {
        const updates = Object.fromEntries(giftLines.map((item) => [item.key, 0]));
        await this.request(window.routes.cart_update_url, { updates });
        if (giftIsCharged) this.showError(this.dataset.giftConfigurationMessage);
      } else if (!giftLines.length && regularTotal >= threshold) {
        await this.request(window.routes.cart_add_url, {
          items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }],
        });
      } else if (giftLines.some((item) => item.quantity !== 1)) {
        const updates = Object.fromEntries(giftLines.map((item) => [item.key, 1]));
        await this.request(window.routes.cart_update_url, { updates });
      }
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.giftSyncing = false;
    }
  }

  async loadRecommendations() {
    const section = this.querySelector('[data-cart-recommendations]');
    if (!section || this.dataset.recommendationsEnabled !== 'true' || section.dataset.loaded === 'true') return;
    section.dataset.loaded = 'true';
    const carousel = section.querySelector('[data-cart-carousel]');
    const fallbackCards = carousel.querySelectorAll('.stll-cart__recommendation').length;
    const productId = this.dataset.firstProductId;
    if (!productId) {
      section.hidden = fallbackCards === 0;
      return;
    }

    try {
      const limit = Number(this.dataset.recommendationLimit) || 4;
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=complementary`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const [{ products = [] }, cart] = await Promise.all([response.json(), this.getCart()]);
      const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));
      const giftVariantId = String(this.dataset.freeGiftVariantId || '');
      const recommendations = products.filter((product) => {
        const variants = (product.variants || []).filter((variant) => variant.available);
        return product.available && variants.length && !cartProductIds.has(String(product.id)) && !variants.some((variant) => String(variant.id) === giftVariantId);
      });

      if (recommendations.length) {
        carousel.replaceChildren(...recommendations.map((product) => this.createRecommendationCard(product, cart.currency)));
      }
      section.hidden = carousel.querySelectorAll('.stll-cart__recommendation').length === 0;
      this.updateCarouselButtons(carousel);
    } catch (_error) {
      section.hidden = fallbackCards === 0;
    }
  }

  createRecommendationCard(product, currency) {
    const card = document.createElement('article');
    card.className = 'stll-cart__recommendation';
    card.dataset.productId = product.id;

    const imageLink = document.createElement('a');
    imageLink.className = 'stll-cart__recommendation-image';
    imageLink.href = product.url;
    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = product.featured_image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 180;
      image.height = 180;
      imageLink.append(image);
    }

    const content = document.createElement('div');
    content.className = 'stll-cart__recommendation-content';
    const title = document.createElement('a');
    title.href = product.url;
    title.textContent = product.title;
    const price = document.createElement('span');
    price.textContent = this.formatMoney(product.price, currency);
    content.append(title, price);

    const variants = (product.variants || []).filter((variant) => variant.available);
    if (variants.length === 1) {
      const add = document.createElement('button');
      add.type = 'button';
      add.dataset.cartUpsellAdd = variants[0].id;
      add.setAttribute('aria-label', `${this.querySelector('[data-cart-recommendations]')?.dataset.addLabel || ''} ${product.title}`.trim());
      add.textContent = '+';
      content.append(add);
    } else {
      const choose = document.createElement('a');
      choose.className = 'stll-cart__choose';
      choose.href = product.url;
      choose.textContent = this.querySelector('[data-cart-recommendations]')?.dataset.chooseLabel || 'Choose';
      content.append(choose);
    }

    card.append(imageLink, content);
    return card;
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(Number(cents) / 100);
    } catch (_error) {
      return `${Number(cents) / 100} ${currency || ''}`.trim();
    }
  }

  updateCarouselButtons(carousel) {
    const section = carousel.closest('[data-cart-recommendations]');
    const previous = section?.querySelector('[data-cart-carousel-prev]');
    const next = section?.querySelector('[data-cart-carousel-next]');
    if (!previous || !next) return;
    previous.disabled = carousel.scrollLeft <= 2;
    next.disabled = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 2;
  }

  createLineEvent(action, line) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return null;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'dialog', lines: [line], promise: deferred.promise }));
    return deferred;
  }

  resolveLineEvent(deferred, cart) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (deferred && CartLinesUpdateEvent) deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
  }

  createDiscountEvent(codes) {
    const { CartDiscountUpdateEvent } = window.StandardEvents || {};
    if (!CartDiscountUpdateEvent) return null;
    const deferred = CartDiscountUpdateEvent.createPromise();
    this.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: codes.map((code) => ({ code })), promise: deferred.promise }));
    return deferred;
  }

  resolveDiscountEvent(deferred, cart) {
    const { CartDiscountUpdateEvent, CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!deferred || !CartDiscountUpdateEvent) return;
    const convert = CartDiscountUpdateEvent.createCartFromAjaxResponse || CartLinesUpdateEvent?.createCartFromAjaxResponse;
    deferred.resolve({ cart: convert ? convert(cart) : cart });
  }

  publishUpdate(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'stll-cart-drawer', cartData: cart });
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
